'use client'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useInterval from 'ui/hooks/useInterval'
import useDidUpdateEffect from 'ui/hooks/useDidUpdateEffect'
import type { CartItem, CartState, CheckoutProduct, DiscountDTO, Language } from 'ecosystem'
import { sumPropValues } from 'shared-utils'
import { useDispatch, useSelector } from 'shared-redux'
import {
  addToCart,
  applyDiscountCode,
  cleanCart,
  removeDiscountCode,
  removeFromCart,
  selectedCart,
  setCartLocale
} from 'shared-redux/state'
import { usePathname } from 'next/navigation'
import { CART_COUNTDOWN } from '../constants'
import type { GaPurchaseOrder } from '../../third-party'
import { useCartTotals } from './useCartTotals'

export type UseCartHook = CartState & {
  handleAddToCart: (cartItem: CartItem) => void
  handleRemoveFromCart: (cartItem: CartItem) => void
  handleApplyDiscountCode: (discount: DiscountDTO) => void
  handleRemoveDiscountCode: () => void
  handleCheckout: () => void
  handleViewCart: () => void
  handlePurchase: (order: GaPurchaseOrder, items: CheckoutProduct[]) => void
  cartTotalDiscount: number
  /** What the products' volume tier ladders took off, reported apart from the code. */
  cartQuantityDiscount: number
  cartCount: number
  isCartOpen: boolean
  hasValidDiscount: boolean
  /** Resolved according to active VAT mode; overrides the persisted gross cartTotal for display. */
  displayCartTotal: number
  excludeVat: boolean
}

interface CartOpts {
  locale?: Language
  onAddToCart?: (newCartItem: CartItem, prevCartItem?: CartItem) => void
  onRemoveFromCart?: (cartItem: CartItem) => void
  onCheckout?: () => void
  onViewCart?: () => void
  onPurchase?: (order: GaPurchaseOrder, items: CheckoutProduct[]) => void
}

const useStdCart = (opts?: CartOpts): UseCartHook => {
  const dispatch = useDispatch()
  const pathname = usePathname()
  const state = useSelector(selectedCart)

  // ****** Localization -- If applies *******

  useEffect(() => {
    if (!opts?.locale) return
    if (!state.cartLocale || state.cartLocale !== opts?.locale) {
      dispatch(cleanCart())
      dispatch(setCartLocale(opts.locale))
    }
  }, [state.cartLocale, dispatch, opts?.locale])

  // ******* LOGIC TO OPEN THE CART WITH COUNTDOWN  *****
  const cartCount = sumPropValues(state.cartItems, 'qty')
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [count, setCount] = useState<number>(0)

  useEffect(() => {
    if (count === CART_COUNTDOWN) {
      setIsCartOpen(false)
    }
  }, [count])

  useInterval(
    () => {
      setCount(count + 1)
    },
    isCartOpen ? 1000 : null
  )

  useDidUpdateEffect(() => {
    if (cartCount && pathname !== '/checkout') {
      setIsCartOpen(true)
      setCount(0)
    }
  }, [cartCount])

  const handleAddToCart = useCallback(
    (cartItem: CartItem) => {
      const existingCartItem = state.cartItems.find((item) => item.id === cartItem.id)

      dispatch(addToCart(cartItem))
      opts?.onAddToCart?.(cartItem, existingCartItem)
    },
    [dispatch, opts, state.cartItems]
  )

  const handleRemoveFromCart = useCallback(
    (cartItem: CartItem) => {
      dispatch(removeFromCart(cartItem))
      opts?.onRemoveFromCart?.(cartItem)
    },
    [dispatch, opts]
  )

  const handleApplyDiscountCode = useCallback(
    (code: DiscountDTO) => {
      dispatch(applyDiscountCode(code))
    },
    [dispatch]
  )

  const handleRemoveDiscountCode = useCallback(() => {
    dispatch(removeDiscountCode())
  }, [dispatch])

  const handleCheckout = useCallback(() => {
    opts?.onCheckout?.()
  }, [opts])

  const handleViewCart = useCallback(() => {
    opts?.onViewCart?.()
  }, [opts])

  const handlePurchase = useCallback(
    (order: GaPurchaseOrder, items: CheckoutProduct[]) => {
      opts?.onPurchase?.(order, items)
    },
    [opts]
  )

  const {
    excludeVat,
    cartTotal: displayCartTotal,
    cartTotalDiscount,
    cartQuantityDiscount
  } = useCartTotals(state.cartItems, state.cartDiscountCode)

  const hasValidDiscount = useMemo(() => {
    return !!(state.cartDiscountCode && cartTotalDiscount > 0)
  }, [state.cartDiscountCode, cartTotalDiscount])

  return {
    handleAddToCart,
    handleRemoveFromCart,
    handleApplyDiscountCode,
    handleRemoveDiscountCode,
    handleCheckout,
    handlePurchase,
    handleViewCart,
    cartTotalDiscount,
    cartQuantityDiscount,
    hasValidDiscount,
    ...state,
    cartCount,
    isCartOpen,
    displayCartTotal,
    excludeVat
  }
}

export default useStdCart
